import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  FormGroup,
  Stack,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Pets as PetsIcon,
  PhotoCamera as PhotoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Reorder as ReorderIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { ShelterAnimal, AnimalPhoto } from '@services/sheltersService';
import { shelterService } from '@services/sheltersService';

interface AnimalEditModalProps {
  open: boolean;
  onClose: () => void;
  animalId: string | null;
  onSave?: (animal: ShelterAnimal) => void;
}

const AnimalEditModal: React.FC<AnimalEditModalProps> = ({
  open,
  onClose,
  animalId,
  onSave
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [profilePhotoId, setProfilePhotoId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    species: 'dog' as 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other', // Standardizat
    breed: '',
    age_months: 0,
    gender: 'male' as 'male' | 'female', // Standardizat (renumit din sex)
    size: 'medie' as 'mica' | 'medie' | 'mare',
    color: '',
    weight_kg: 0,
    height_cm: 0,
    sterilized: false,
    vaccinated: false,
    microchipped: false,
    chip_number: '',
    passport_number: '',
    dewormed: false,
    deparasit_extern: false,
    vaccinated_rabies: false,
    special_needs: '',
    description: '',
    status: 'available' as 'available' | 'reserved' | 'adopted', // Standardizat
    adoptable: true,
    intake_date: '',
    adoption_date: '',
    photos: [] as AnimalPhoto[]
  });

  useEffect(() => {
    if (open && animalId) {
      loadAnimalData();
    }
  }, [open, animalId]);

  const loadAnimalData = async () => {
    if (!animalId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await shelterService.getAnimalById(animalId);
      const animal = response.data || response;
      
      // Conversie pentru compatibilitate cu valorile vechi
      const normalizedSpecies = animal.species || 'dog';
      const normalizedGender = animal.gender || (animal as any).sex === 'M' ? 'male' : (animal as any).sex === 'F' ? 'female' : 'male';
      const normalizedStatus = animal.status || 'available';
      
      // Mapare pentru valori vechi
      const speciesMap: Record<string, string> = {
        'caine': 'dog',
        'pisica': 'cat',
        'altul': 'other'
      };
      const statusMap: Record<string, string> = {
        'Disponibil': 'available',
        'Rezervat': 'reserved',
        'Adoptat': 'adopted'
      };
      
      const finalSpecies = (speciesMap[normalizedSpecies] || normalizedSpecies) as any;
      const finalStatus = (statusMap[normalizedStatus] || normalizedStatus) as any;
      
      setFormData({
        name: animal.name || '',
        species: finalSpecies,
        breed: animal.breed || '',
        age_months: animal.age_months || 0,
        gender: normalizedGender as any,
        size: animal.size || 'medie',
        color: animal.color || '',
        weight_kg: animal.weight_kg || 0,
        height_cm: animal.height_cm || 0,
        sterilized: animal.sterilized || false,
        vaccinated: animal.vaccinated || false,
        microchipped: animal.microchipped || false,
        chip_number: animal.chip_number || '',
        passport_number: animal.passport_number || '',
        dewormed: animal.dewormed || false,
        deparasit_extern: animal.deparasit_extern || false,
        vaccinated_rabies: animal.vaccinated_rabies || false,
        special_needs: animal.special_needs || '',
        description: animal.description || '',
        status: finalStatus,
        adoptable: animal.adoptable !== false,
        intake_date: animal.intake_date ? animal.intake_date.split('T')[0] : '',
        adoption_date: animal.adoption_date ? animal.adoption_date.split('T')[0] : '',
        photos: animal.photos || []
      });
      
      // Setează poza de profil (prima poza sau cea marcată ca profil)
      if (animal.photos && animal.photos.length > 0) {
        const profilePhoto = animal.photos.find((p: any) => p.is_profile) || animal.photos[0];
        setProfilePhotoId(profilePhoto.id);
      }
    } catch (err) {
      console.error('Error loading animal data:', err);
      setError('Eroare la încărcarea datelor animalului');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validare obligatorie
      if (!formData.name.trim()) {
        setError('Numele este obligatoriu');
        return;
      }

      if (!formData.species) {
        setError('Specia este obligatorie');
        return;
      }

      if (!formData.gender) {
        setError('Genul este obligatoriu');
        return;
      }

      if (!formData.status) {
        setError('Statusul este obligatoriu');
        return;
      }

      const updateData = {
        ...formData,
        age_months: Number(formData.age_months),
        weight_kg: Number(formData.weight_kg),
        height_cm: Number(formData.height_cm),
        // Marchează poza de profil
        photos: formData.photos.map(photo => ({
          ...photo,
          is_cover: photo.id === profilePhotoId
        }))
      };

      const response = await shelterService.updateAnimal(animalId!, updateData);
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSave?.(response.data || response);
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error saving animal:', err);
      setError('Eroare la salvarea animalului');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingPhotos(true);
      
      // Folosește uploadAnimalPhotos pentru a trimite pozele la animalul specific
      const filesArray = Array.from(files);
      const response = await shelterService.uploadAnimalPhotos(animalId!, filesArray);
      
      // Adaugă noile poze la lista existentă
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...response.data]
      }));

      // Dacă nu există poza de profil, setează prima poza nouă
      if (!profilePhotoId && response.data.length > 0) {
        setProfilePhotoId(response.data[0].id);
      }
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Eroare la încărcarea fotografiilor');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handlePhotoDelete = (photoId: string) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(photo => photo.id !== photoId)
    }));
    
    // Dacă ștergi poza de profil, selectează prima poza rămasă
    if (profilePhotoId === photoId) {
      const remainingPhotos = formData.photos.filter(photo => photo.id !== photoId);
      setProfilePhotoId(remainingPhotos.length > 0 ? remainingPhotos[0].id : null);
    }
  };

  const handleSetProfilePhoto = async (photoId: string) => {
    try {
      await shelterService.setCoverPhoto(animalId!, photoId);
      setProfilePhotoId(photoId);
    } catch (err) {
      console.error('Error setting cover photo:', err);
      setError('Eroare la setarea pozei de profil');
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const items = Array.from(formData.photos);
    const [reorderedItem] = items.splice(draggedIndex, 1);
    items.splice(dropIndex, 0, reorderedItem);

    // Actualizează sort_order pentru toate pozele
    const updatedPhotos = items.map((photo, index) => ({
      ...photo,
      sort_order: index
    }));

    setFormData(prev => ({
      ...prev,
      photos: updatedPhotos
    }));
    
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PetsIcon />
            </Avatar>
            <Typography variant="h5" fontWeight="bold">
              Editează Animal
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Animalul a fost actualizat cu succes!
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Informații generale */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informații Generale
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Nume animal *"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    sx={{ minWidth: 200, flex: 1 }}
                  />
                  <FormControl required sx={{ minWidth: 150 }}>
                    <InputLabel>Specie *</InputLabel>
                    <Select
                      value={formData.species}
                      onChange={(e) => handleInputChange('species', e.target.value)}
                    >
                      <MenuItem value="dog">Câine</MenuItem>
                      <MenuItem value="cat">Pisică</MenuItem>
                      <MenuItem value="bird">Pasăre</MenuItem>
                      <MenuItem value="rabbit">Iepure</MenuItem>
                      <MenuItem value="horse">Cal</MenuItem>
                      <MenuItem value="other">Altul</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Rasă"
                    value={formData.breed}
                    onChange={(e) => handleInputChange('breed', e.target.value)}
                    sx={{ minWidth: 200, flex: 1 }}
                  />
                  <TextField
                    label="Vârstă (luni)"
                    type="number"
                    value={formData.age_months}
                    onChange={(e) => handleInputChange('age_months', parseInt(e.target.value) || 0)}
                    sx={{ minWidth: 150 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl required>
                    <FormLabel>Gen *</FormLabel>
                    <RadioGroup
                      row
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    >
                      <FormControlLabel value="male" control={<Radio />} label="Mascul" />
                      <FormControlLabel value="female" control={<Radio />} label="Femelă" />
                    </RadioGroup>
                  </FormControl>
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Talie</InputLabel>
                    <Select
                      value={formData.size}
                      onChange={(e) => handleInputChange('size', e.target.value)}
                    >
                      <MenuItem value="mica">Mică</MenuItem>
                      <MenuItem value="medie">Medie</MenuItem>
                      <MenuItem value="mare">Mare</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Culoare"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    sx={{ minWidth: 200, flex: 1 }}
                  />
                  <TextField
                    label="Greutate (kg)"
                    type="number"
                    value={formData.weight_kg}
                    onChange={(e) => handleInputChange('weight_kg', parseFloat(e.target.value) || 0)}
                    sx={{ minWidth: 150 }}
                  />
                </Box>

                <TextField
                  label="Descriere"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  fullWidth
                />
              </Box>
            </CardContent>
          </Card>

          {/* Date de identificare și sănătate */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 300 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Date de Identificare
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.microchipped}
                        onChange={(e) => handleInputChange('microchipped', e.target.checked)}
                      />
                    }
                    label="Microcip"
                  />
                  {formData.microchipped && (
                    <TextField
                      label="Număr microcip"
                      value={formData.chip_number}
                      onChange={(e) => handleInputChange('chip_number', e.target.value)}
                      fullWidth
                    />
                  )}
                  <TextField
                    label="Număr pasaport"
                    value={formData.passport_number}
                    onChange={(e) => handleInputChange('passport_number', e.target.value)}
                    fullWidth
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 300 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Stare de Sănătate
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.vaccinated}
                        onChange={(e) => handleInputChange('vaccinated', e.target.checked)}
                      />
                    }
                    label="Vaccinat"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.sterilized}
                        onChange={(e) => handleInputChange('sterilized', e.target.checked)}
                      />
                    }
                    label="Sterilizat"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.dewormed}
                        onChange={(e) => handleInputChange('dewormed', e.target.checked)}
                      />
                    }
                    label="Deparazitat"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.deparasit_extern}
                        onChange={(e) => handleInputChange('deparasit_extern', e.target.checked)}
                      />
                    }
                    label="Deparazitare externă"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.vaccinated_rabies}
                        onChange={(e) => handleInputChange('vaccinated_rabies', e.target.checked)}
                      />
                    }
                    label="Vaccinat împotriva rabiei"
                  />
                </FormGroup>
                <TextField
                  label="Nevoi speciale"
                  multiline
                  rows={2}
                  value={formData.special_needs}
                  onChange={(e) => handleInputChange('special_needs', e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>
          </Box>

          {/* Status și administrare */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 300 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status și Administrare
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Status *</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="Disponibil">Disponibil</MenuItem>
                      <MenuItem value="Rezervat">Rezervat</MenuItem>
                      <MenuItem value="Adoptat">Adoptat</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.adoptable}
                        onChange={(e) => handleInputChange('adoptable', e.target.checked)}
                      />
                    }
                    label="Adoptabil"
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Data intrării"
                      type="date"
                      value={formData.intake_date}
                      onChange={(e) => handleInputChange('intake_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Data adoptiei"
                      type="date"
                      value={formData.adoption_date}
                      onChange={(e) => handleInputChange('adoption_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Galerie foto */}
            <Card sx={{ flex: 1, minWidth: 300 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Galerie Foto
                </Typography>
                
                {/* Upload button */}
                <Box mb={2}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="photo-upload"
                    type="file"
                    multiple
                    onChange={handlePhotoUpload}
                  />
                  <label htmlFor="photo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploadingPhotos ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      fullWidth
                      disabled={uploadingPhotos}
                    >
                      {uploadingPhotos ? 'Se încarcă...' : 'Adaugă Fotografii (Multiple)'}
                    </Button>
                  </label>
                </Box>

                {/* Photo gallery with drag & drop */}
                {formData.photos.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      minHeight: 100
                    }}
                  >
                    {formData.photos
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((photo, index) => (
                      <Paper
                        key={`photo-${photo.id}-${index}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'grab',
                          border: profilePhotoId === photo.id ? '3px solid' : '1px solid',
                          borderColor: profilePhotoId === photo.id ? 'primary.main' : 'divider',
                          opacity: draggedIndex === index ? 0.5 : 1,
                          transition: 'all 0.2s'
                        }}
                      >
                        <img
                          src={(() => {
                            if (photo.file_path.startsWith('http')) return photo.file_path;
                            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
                            const baseURL = API_URL.replace('/api/v1', '');
                            return `${baseURL}${photo.file_path}`;
                          })()}
                          alt={photo.caption || 'Animal photo'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        
                        {/* Overlay cu acțiuni */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        >
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Setează ca poza de profil">
                              <IconButton
                                size="small"
                                color={profilePhotoId === photo.id ? 'warning' : 'default'}
                                onClick={() => handleSetProfilePhoto(photo.id)}
                              >
                                {profilePhotoId === photo.id ? <StarIcon /> : <StarBorderIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Șterge poza">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handlePhotoDelete(photo.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        {/* Indicator poza de profil */}
                        {profilePhotoId === photo.id && (
                          <Chip
                            label="Profil"
                            size="small"
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        )}

                        {/* Drag handle */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            color: 'white',
                            opacity: 0.7
                          }}
                        >
                          <ReorderIcon fontSize="small" />
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    py={4}
                    color="text.secondary"
                  >
                    <PhotoIcon sx={{ fontSize: 48, mb: 1 }} />
                    <Typography variant="body2">
                      Nu există poze încărcate
                    </Typography>
                  </Box>
                )}

                {/* Instrucțiuni */}
                {formData.photos.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      💡 Drag & drop pentru reordonare • Click pe stea pentru poza de profil • Click pe șterge pentru eliminare
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Anulează
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {saving ? 'Salvează...' : 'Salvează Modificările'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimalEditModal;
